import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type OptionValue = string | number;

export interface AdministrativeAreaOption {
  value: OptionValue;
  label: string;
  parentId?: OptionValue | null;
  type?: string;
  children?: AdministrativeAreaOption[];
  [key: string]: unknown;
}

interface AdministrativeAreaTreeSelectProps {
  id: string;
  tree?: AdministrativeAreaOption[];
  value?: AdministrativeAreaOption | null;
  placeholder?: string;
  clearLabel: string;
  isDisabled?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  onChange: (value: AdministrativeAreaOption | null) => void;
}

const toSelectValue = (value?: OptionValue | null) =>
  value === undefined || value === null ? undefined : String(value);

const findInTree = (
  tree: AdministrativeAreaOption[],
  value: OptionValue
): AdministrativeAreaOption | null => {
  const stack = [...tree];
  while (stack.length) {
    const node = stack.shift();
    if (!node) {
      continue;
    }
    if (node.value === value || String(node.value) === String(value)) {
      return node;
    }
    if (node.children?.length) {
      stack.push(...node.children);
    }
  }
  return null;
};

const findInLevel = (options: AdministrativeAreaOption[], value: string) =>
  options.find(option => toSelectValue(option.value) === value) ?? null;

const buildLevelsFromValue = (
  tree: AdministrativeAreaOption[],
  value: AdministrativeAreaOption
) => {
  let option = findInTree(tree, value.value);
  if (!option) {
    return {
      levelOptions: [tree],
      levelValue: [],
    };
  }

  const levelValue = [option];
  const levelOptions = option.children?.length ? [option.children] : [];

  while (option.parentId !== undefined && option.parentId !== null) {
    const parent = findInTree(tree, option.parentId);
    if (!parent) {
      break;
    }

    levelValue.unshift(parent);
    if (parent.children?.length) {
      levelOptions.unshift(parent.children);
    }
    option = parent;
  }

  levelOptions.unshift(tree);

  return {
    levelOptions,
    levelValue,
  };
};

export const AdministrativeAreaTreeSelect = ({
  id,
  tree = [],
  value,
  placeholder = "",
  clearLabel,
  isDisabled = false,
  hasError = false,
  onBlur,
  onChange,
}: AdministrativeAreaTreeSelectProps) => {
  const [levelOptions, setLevelOptions] = useState<AdministrativeAreaOption[][]>([tree]);
  const [levelValue, setLevelValue] = useState<AdministrativeAreaOption[]>(value ? [value] : []);

  useEffect(() => {
    if (value) {
      const nextLevels = buildLevelsFromValue(tree, value);
      setLevelOptions(nextLevels.levelOptions);
      setLevelValue(nextLevels.levelValue);
      return;
    }

    setLevelOptions([tree]);
    setLevelValue([]);
  }, [tree, value]);

  const handleSelect = (selectedValue: string, level: number) => {
    const option = findInLevel(levelOptions[level] ?? [], selectedValue);
    if (!option) {
      return;
    }

    const nextValue = [...levelValue.slice(0, level), option];
    const nextOptions = levelOptions.slice(0, level + 1);

    if (option.children?.length) {
      nextOptions.push(option.children);
    }

    setLevelValue(nextValue);
    setLevelOptions(nextOptions);
    onChange(option);
  };

  const handleClear = () => {
    setLevelOptions([tree]);
    setLevelValue([]);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      {levelOptions.map((options, level) => {
        const selectedValue = toSelectValue(levelValue[level]?.value);
        const selectId = level === 0 ? id : `${id}-${level}`;
        const isSelectDisabled = isDisabled || options.length === 0;

        return (
          <div
            className="flex items-center gap-2"
            key={`${selectId}-${toSelectValue(levelValue[level - 1]?.value) ?? "root"}`}
          >
            <Select
              value={selectedValue}
              onValueChange={nextValue => handleSelect(nextValue, level)}
              disabled={isSelectDisabled}
            >
              <SelectTrigger
                id={selectId}
                aria-invalid={hasError}
                onBlur={onBlur}
                className={cn(
                  "h-10 min-w-0 flex-1 border-border",
                  hasError && "border-destructive ring-destructive/20"
                )}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-72">
                {options.map(option => {
                  const optionValue = toSelectValue(option.value);
                  if (!optionValue) {
                    return null;
                  }

                  return (
                    <SelectItem key={optionValue} value={optionValue}>
                      {option.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {level === 0 && (
              <WFPButton
                type="button"
                variant="ghost"
                size="icon"
                aria-label={clearLabel}
                onClick={handleClear}
                className={cn(
                  "size-10 text-muted-foreground",
                  !levelValue.length && "pointer-events-none invisible"
                )}
              >
                <X className="size-4" />
              </WFPButton>
            )}
          </div>
        );
      })}
    </div>
  );
};

export type { AdministrativeAreaTreeSelectProps };
